import { FulfillmentStatus, OrderDeliveryType } from "@dashboard/graphql";

type MetadataEntries = ReadonlyArray<{ key: string; value: string } | null> | null | undefined;

// Order metadata where the backend keeps the GLS pickup point ("Ponto de
// Recolha") address staff enter when shipping; it is shown in the shipping email.
export const PONTO_RECOLHA_ADDRESS_KEY = "ponto_recolha_address";

// Set once staff tell an in-store pickup customer their order is ready to collect.
// Like `orderReceivedMarked`, it persists the stepper step and is shared.
export const PICKUP_READY_NOTIFIED_KEY = "pickupReadyNotified";

const getMetadataValue = (metadata: MetadataEntries, key: string): string =>
  (metadata ?? []).find(entry => entry?.key === key)?.value ?? "";

export const getRecordedPontoRecolhaAddress = (metadata: MetadataEntries): string =>
  getMetadataValue(metadata, PONTO_RECOLHA_ADDRESS_KEY).trim();

export const isPickupReadyNotified = (metadata: MetadataEntries): boolean =>
  getMetadataValue(metadata, PICKUP_READY_NOTIFIED_KEY) === "true";

export const isParcelShopDelivery = (deliveryType: OrderDeliveryType | undefined): boolean =>
  deliveryType === OrderDeliveryType.PARCEL_SHOP;

export const isStorePickup = (deliveryType: OrderDeliveryType | undefined): boolean =>
  deliveryType === OrderDeliveryType.STORE_PICKUP;

// Order private metadata holding the PIN an in-store pickup customer shows at the
// store. The backend emails it with the "ready for pickup" notice (generating it
// if missing) and the pickup ticket prints it, so staff can match the two.
export const PICKUP_PIN_KEY = "pickup_pin";

const PICKUP_PIN_LENGTH = 4;

export const getPickupPin = (privateMetadata: MetadataEntries): string =>
  getMetadataValue(privateMetadata, PICKUP_PIN_KEY).trim();

export const generatePickupPin = (): string => {
  const [random] = crypto.getRandomValues(new Uint32Array(1));

  return String(random % 10 ** PICKUP_PIN_LENGTH).padStart(PICKUP_PIN_LENGTH, "0");
};

type ShippedFulfillment = { id: string; status: FulfillmentStatus; trackingNumber: string } | null;

// The shipment whose shipping email can be (re)sent: the tracked one, or for
// pickup-point orders (which may ship without a tracking number) any active one.
export const getResendableFulfillment = <T extends ShippedFulfillment>(
  fulfillments: readonly T[] | null | undefined,
  isParcelShop: boolean,
): T | undefined => {
  const active = (fulfillments ?? []).filter(
    fulfillment => fulfillment && fulfillment.status !== FulfillmentStatus.CANCELED,
  );

  return (
    active.find(fulfillment => !!fulfillment?.trackingNumber) ??
    (isParcelShop ? active[0] : undefined)
  );
};

// A pickup-point order already shipped whose shop address was never recorded, so
// the customer hasn't been told where to collect it.
export const isPontoRecolhaAddressMissing = (
  order:
    | {
        deliveryType?: OrderDeliveryType;
        metadata: MetadataEntries;
        fulfillments: ReadonlyArray<ShippedFulfillment>;
      }
    | null
    | undefined,
): boolean =>
  !!order &&
  isParcelShopDelivery(order.deliveryType) &&
  !getRecordedPontoRecolhaAddress(order.metadata) &&
  !!getResendableFulfillment(order.fulfillments, true);
